import { useMyBubblesQuery } from "@crowdcoursing/api/graphql";
import { useOrganizationCreateOrganization } from "@crowdcoursing/api/openapi";
import { Button, CircularProgress, Grid } from "@mui/material";
import { useCallback, useState } from "react";

import { CharacterLimitedTextField as TextField } from "../../ui/atoms/CharacterLimitedTextField/CharacterLimitedTextField";
import { SimpleSelect } from "../../ui/atoms/Form/SimpleSelect/SimpleSelect";

// Centralized strings for internationalization
const tParentOrganizationLabel = "Parent Organization";
const tOrganizationNameLabel = "Organization Name";
const tOrganizationDescriptionLabel = "Organization Description";
const tOrganizationAddressLabel = "Organization Address";
const tCreateOrganization = "Create Organization";
const tNameRequired = "Name is required";
const tAddressRequired = "Address is required";
const tDescriptionRequired = "Description is required";
const tNone = "None";

/** @deprecated We are phasing out `components/**` */
interface FormValues {
  address: string;
  bubbleId?: string;
  canCreateEvents: boolean;
  description: string;
  name: string;
}

/** @deprecated We are phasing out `components/**` */
interface FormErrors {
  address?: string;
  description?: string;
  name?: string;
  bubbleId?: string;
}

/** @deprecated We are phasing out `components/**` */
interface CreateOrganizationProps {
  onClose: () => void;
}

/** @deprecated We are phasing out `components/**` */
const CreateOrganization = ({ onClose }: CreateOrganizationProps) => {
  const { data: myBubbles } = useMyBubblesQuery();
  const bubbles = myBubbles?.me.bubbles ?? [];
  const transformedBubbles = bubbles
    .map((bubble) => ({
      id: bubble.id,
      value: bubble.id === "c0524501-ff1c-4b76-b384-1317e342aa48" ? tNone : bubble.name,
    }))
    .sort((a, b) => {
      // Put Community bubble first if it exists
      if (a.id === "c0524501-ff1c-4b76-b384-1317e342aa48") {
        return -1;
      }
      if (b.id === "c0524501-ff1c-4b76-b384-1317e342aa48") {
        return 1;
      }
      // Sort everything else alphabetically by name
      return a.value.localeCompare(b.value);
    });

  const communityBubble = bubbles.find((bubble) => bubble.id === "c0524501-ff1c-4b76-b384-1317e342aa48");
  const [formValues, setFormValues] = useState<FormValues>({
    address: "",
    bubbleId: communityBubble?.id,
    canCreateEvents: false,
    description: "",
    name: "",
  });
  const [errors, setErrors] = useState<FormErrors>({});

  const { mutate: createOrganization, isPending } = useOrganizationCreateOrganization({
    onError: (error) => {
      console.error("Error creating organization:", error);
    },
    onSuccess: () => {
      onClose();
    },
  });

  const handleFormChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors((prev) => ({
        ...prev,
        [name]: undefined,
      }));
    }
  };

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      const validationErrors: FormErrors = {};
      if (!formValues.name) {
        validationErrors.name = tNameRequired;
      }
      if (!formValues.address) {
        validationErrors.address = tAddressRequired;
      }
      if (!formValues.description) {
        validationErrors.description = tDescriptionRequired;
      }
      setErrors(validationErrors);
      if (Object.keys(validationErrors).length === 0) {
        createOrganization({ body: formValues });
      }
    },
    [createOrganization, formValues],
  );

  return (
    <Grid container justifyContent="center">
      <Grid
        size={{
          md: 8,
          xs: 10,
        }}
      >
        <form onSubmit={handleSubmit}>
          <Grid size={12}>
            <SimpleSelect
              error={!!errors.bubbleId}
              helperText={errors.bubbleId}
              label={tParentOrganizationLabel}
              onChange={(value) => {
                setFormValues((prev) => ({
                  ...prev,
                  bubbleId: value,
                }));
                if (errors.bubbleId) {
                  setErrors((prev) => ({
                    ...prev,
                    bubbleId: undefined,
                  }));
                }
              }}
              required
              selectItems={transformedBubbles}
              value={formValues.bubbleId || ""}
            />
          </Grid>
          <TextField
            disabled={isPending}
            error={!!errors.name}
            fullWidth
            helperText={errors.name}
            id="name"
            inputProps={{ maxLength: 200 }}
            label={tOrganizationNameLabel}
            margin="normal"
            name="name"
            onChange={handleFormChange}
            value={formValues.name}
          />
          <TextField
            disabled={isPending}
            error={!!errors.description}
            fullWidth
            helperText={errors.description}
            id="description"
            inputProps={{ maxLength: 1000 }}
            label={tOrganizationDescriptionLabel}
            margin="normal"
            name="description"
            onChange={handleFormChange}
            value={formValues.description}
          />
          <TextField
            disabled={isPending}
            error={!!errors.address}
            fullWidth
            helperText={errors.address}
            id="address"
            inputProps={{ maxLength: 250 }}
            label={tOrganizationAddressLabel}
            margin="normal"
            name="address"
            onChange={handleFormChange}
            value={formValues.address}
          />
          <Grid container justifyContent="flex-end" marginTop={2}>
            <Button disabled={isPending} type="submit" variant="contained">
              {isPending ? <CircularProgress size={24} /> : tCreateOrganization}
            </Button>
          </Grid>
        </form>
      </Grid>
    </Grid>
  );
};

/** @deprecated We are phasing out `components/**` */
export default CreateOrganization;
